import { useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { motion } from "framer-motion";
import { Sparkles, Plus, Hourglass } from "lucide-react";

function useCount(target, dur = 1100) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (target == null) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setV(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, dur]);
  return v;
}

export default function BioAgeCard({ estimate, profile, onEdit }) {
  const { t } = useLang();
  const hasEstimate = !!estimate;
  const bio = useCount(hasEstimate ? estimate.biologicalAge : 0);
  const chrono = useCount(hasEstimate ? estimate.chronologicalAge : 0);
  const delta = estimate?.delta ?? 0;

  if (!hasEstimate) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative flex flex-col justify-between overflow-hidden rounded-[28px] border border-dashed border-[color:var(--sentinel-accent)] bg-white p-7"
        data-testid="bio-age-empty"
      >
        <div>
          <span className="overline">{t.bioAge.overline}</span>
          <h3 className="mt-3 text-2xl text-zinc-900">
            {t.bioAge.emptyTitle}
          </h3>
          <p className="mt-2 max-w-[280px] text-sm leading-relaxed text-zinc-500">
            {t.bioAge.emptyBody}
          </p>
        </div>
        <button
          onClick={onEdit}
          className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-zinc-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[color:var(--sentinel)]"
          data-testid="bio-age-add-cta"
        >
          <Plus className="h-4 w-4" />
          {t.bioAge.computeCta}
        </button>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-8 -bottom-8 h-40 w-40 rounded-full"
          style={{ background: "radial-gradient(closest-side, rgba(196,192,245,0.5), transparent)" }}
        />
        <Hourglass
          aria-hidden="true"
          className="pointer-events-none absolute right-6 top-6 h-14 w-14 text-[color:var(--sentinel-accent)] opacity-50"
        />
      </motion.div>
    );
  }

  const youngerColor =
    delta < -1 ? "text-emerald-600" : delta > 1 ? "text-amber-600" : "text-zinc-500";

  const deltaLabel =
    delta < -0.5
      ? t.bioAge.deltaYounger.replace("{years}", Math.abs(delta).toFixed(1))
      : delta > 0.5
      ? t.bioAge.deltaOlder.replace("{years}", delta.toFixed(1))
      : t.bioAge.deltaEqual;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="relative flex flex-col justify-between overflow-hidden rounded-[28px] border border-zinc-200 bg-zinc-950 p-7 text-white"
      data-testid="bio-age-card"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(420px 240px at 90% 0%, rgba(196,192,245,0.35), transparent), radial-gradient(380px 220px at 0% 110%, rgba(81,75,186,0.6), transparent)",
        }}
      />
      <div className="relative">
        <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[color:var(--sentinel-accent)]">
          {t.bioAge.overline}
        </span>
        <div className="mt-4 flex items-end gap-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-400">
              {t.bioAge.biological}
            </p>
            <div className="mt-1 font-mono text-[64px] font-semibold leading-none tracking-tight text-white">
              {bio.toFixed(1)}
            </div>
          </div>
          <div className="pb-2 text-zinc-500">vs</div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-400">
              {t.bioAge.real}
            </p>
            <div className="mt-1 font-mono text-[36px] font-semibold leading-none tracking-tight text-zinc-300">
              {Math.round(chrono)}
            </div>
          </div>
        </div>
        <div className={`mt-5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium ${youngerColor}`}>
          <Sparkles className="h-3 w-3" />
          {deltaLabel}
        </div>
      </div>

      <div className="relative mt-6 border-t border-white/10 pt-4 text-[11px] leading-relaxed text-zinc-400">
        <p>{t.bioAge.methodology}</p>
        <button
          onClick={onEdit}
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[color:var(--sentinel-accent)] hover:text-white"
          data-testid="bio-age-edit"
        >
          {t.bioAge.edit}
        </button>
      </div>
    </motion.div>
  );
}
